import { DatePipe, DecimalPipe } from '@angular/common';
import { Component, effect, inject, OnInit, signal } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatMenuModule } from '@angular/material/menu';
import { Sort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { TableStateService } from '@core/services/table-state.service';
import { TranslateModule } from '@ngx-translate/core';
import { DatatableBaseComponent } from '@shared/components/datatable-base/datatable-base.component';
import { DatatableComponent } from '@shared/components/datatable/datatable.component';
import {
  IDataRefreshRequestParams,
  IDatatableConfiguration,
  IRowClickEvent,
  TDatatableColumn,
} from '@shared/components/datatable/datatable.interface';
import { ISkeletonLoaderConfig } from '@shared/components/skeleton-loader/skeleton-loader.interface';
import { combineLatest, distinctUntilChanged, filter } from 'rxjs';

import { IUserGroup } from '@core/interfaces/user-group.interface';
import { UuidUtilitiesService } from '@utilities/uuid-utilities.service';
import { AdminUserGroupEditDialogComponent } from './admin-user-group-edit-dialog/admin-user-group-edit-dialog.component';
import { AdminUserGroupsTabStore } from './admin-user-groups-tab.store';

@Component({
  imports: [
    DatatableComponent,
    MatButtonModule,
    MatMenuModule,
    MatDialogModule,
    TranslateModule,
    DatePipe,
    DecimalPipe,
  ],
  providers: [AdminUserGroupsTabStore],
  selector: 'app-admin-user-groups-tab',
  styleUrl: './admin-user-groups-tab.component.scss',
  templateUrl: './admin-user-groups-tab.component.html',
})
export class AdminUserGroupsTabComponent extends DatatableBaseComponent implements OnInit {
  public readonly dataSource = signal<MatTableDataSource<IUserGroup>>(new MatTableDataSource<IUserGroup>([]));
  public readonly loadedTableData = signal<boolean>(false);
  public readonly store = inject(AdminUserGroupsTabStore);

  public datatableColumns: TDatatableColumn<IUserGroup, string>[] = [
    { id: 'name' },
    { id: 'currentConcurrentCampaigns' },
    { id: 'effectiveMaxConcurrentCampaigns' },
    { id: 'currentCreatorChannels' },
    { id: 'effectiveMaxCreatorChannels' },
    { id: 'currentSeats' },
    { id: 'effectiveMaxSeats' },
    { id: 'endDateWrite' },
    { id: 'endDateRead' },
    { id: 'tokenUsage' },
  ];

  public datatableConfig!: IDatatableConfiguration<IUserGroup>;

  public readonly skeletonLoaderConfig: ISkeletonLoaderConfig = {
    columns: [
      { borderRadius: '4px', className: 'w-[22%]', rowPadding: '18px 0', textWidth: '120px', type: 'text' },
      { borderRadius: '4px', className: 'w-[10%]', textWidth: '60px', type: 'text' },
      { borderRadius: '4px', className: 'w-[10%]', textWidth: '60px', type: 'text' },
      { borderRadius: '4px', className: 'w-[10%]', textWidth: '60px', type: 'text' },
      { borderRadius: '4px', className: 'w-[8%]', textWidth: '60px', type: 'text' },
      { borderRadius: '4px', className: 'w-[8%]', textWidth: '60px', type: 'text' },
      { borderRadius: '4px', className: 'w-[8%]', textWidth: '60px', type: 'text' },
      { borderRadius: '4px', className: 'w-[8%]', textWidth: '80px', type: 'text' },
      { borderRadius: '4px', className: 'w-[8%]', textWidth: '80px', type: 'text' },
      { borderRadius: '4px', className: 'w-[8%]', textWidth: '80px', type: 'text' },
    ],
    filters: [],
    rows: 5,
    showFilters: false,
    showHeaderControls: true,
    showSearchField: true,
  };

  private readonly dialog = inject(MatDialog);
  private readonly tableStateService: TableStateService = inject(TableStateService);
  private readonly tableStorageKey: string = this.tableStateService.buildKey('admin-user-group', 'groups-tab');

  constructor() {
    super();

    effect(() => {
      const data = this.store.data$();

      if (data && (data.length > 0 || !this.store.dataLoading$())) {
        this.dataSource().data = data;
        this.loadedTableData.set(true);
      }
    });
  }

  public ngOnInit(): void {
    this.setDatatableConfig();

    this.subscriptions.push(
      combineLatest({
        datatable: this.datatableParams$.pipe(filter(Boolean)),
      })
        .pipe(distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)))
        .subscribe(({ datatable }) => {
          this.store.fetchUserGroups(this.prepareFetchParams(datatable));
        }),
    );
  }

  public onRowClick(row: IRowClickEvent<IUserGroup, string>): void {
    const dialogRef = this.dialog.open(AdminUserGroupEditDialogComponent, {
      data: {
        userGroup: row.row,
      },
      width: '800px',
    });

    dialogRef.afterClosed().subscribe((result: boolean | undefined) => {
      if (!result) {
        return;
      }

      const currentParams = this.datatableParams$.value;

      if (currentParams) {
        this.store.fetchUserGroups(this.prepareFetchParams(currentParams));
      }
    });
  }

  private prepareFetchParams(params: IDataRefreshRequestParams): {
    filters?: Record<string, unknown>;
    joins?: string[];
    limit: number;
    page: number;
    search?: { searchedFields: string[]; searchText: string };
    sort: Sort[];
  } {
    const searchedFields = UuidUtilitiesService.isUuidV4(params.search ?? '') ? ['id'] : ['name__ilike'];

    return {
      joins: ['all'],
      limit: params.limit,
      page: params.page,
      sort: [params.sort],
      ...(params.search ? { search: { searchedFields, searchText: params.search } } : {}),
      ...(params.filters && Object.keys(params.filters).length ? { filters: params.filters } : {}),
    };
  }

  private setDatatableConfig(): void {
    this.datatableConfig = {
      defaultSort: { active: 'name', direction: 'asc' },
      filters: [],
      isClientSide: false,
      skipInitialRequest: false,
      storageKey: this.tableStorageKey,
    } satisfies IDatatableConfiguration<IUserGroup>;
  }
}
